import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import Whatsapp from '../svg/Whatsapp';
import WhatsAppModal from './WhatsAppConsentModal';
import { useAuth } from '../hooks/useAuth';
import {
  getWhatsappConsentStatus,
  whatsappConnect,
  whatsappConsent,
} from '../utils/api';
import { toast } from 'react-toastify';

interface WhatsAppButtonProps {
  variant?: 'mobile' | 'desktop';
  onWhatsAppSave?: (country: string, phoneNumber: string) => void;
  roomId?: string;
}

const WhatsAppButton: React.FC<WhatsAppButtonProps> = ({
  variant = 'desktop',
  onWhatsAppSave,
  roomId,
}) => {
  const theme = useTheme();
  const { authUser } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkConsentStatus = async () => {
      if (!authUser?.uid) return;

      const response = await getWhatsappConsentStatus(authUser.uid);

      setHasConsent(response.consent_status);
    };

    checkConsentStatus();
  }, [authUser?.uid]);

  const handleConnectWhatsApp = async () => {
    setIsLoading(true);

    try {
      await whatsappConnect(roomId);
      toast.success('WhatsApp connected!');
    } catch (err) {
      toast.error('Oops! We couldn\'t connect WhatsApp. Please try again');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = async () => {
    if (!authUser?.uid) {
      return;
    }

    if (hasConsent) {
      handleConnectWhatsApp();
    } else {
      setModalOpen(true);
    }
  };

  const handleSave = async (countryCode: string, phoneNumber: string) => {
    if (!authUser?.uid) return;

    setIsLoading(true);
    setError(null);

    try {
      const fullNumber = `${countryCode}${phoneNumber}`;

      await whatsappConsent(authUser.uid, fullNumber);

      setHasConsent(true);

      if (onWhatsAppSave) {
        onWhatsAppSave(countryCode, phoneNumber);
      }

      setModalOpen(false);

      toast.success('WhatsApp consent saved successfully');

      handleConnectWhatsApp();
    } catch (err) {
      setError('Failed to save WhatsApp number. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (variant === 'mobile') {
    return (
      <>
        <Box
          onClick={handleClick}
          sx={{
            display: { xs: 'flex', md: 'none' },
            padding: '8px',
            background:
              theme.palette.background['paper-elevation-2'] || 'white',
            borderRadius: '8px',
            border: `1px solid ${theme.palette.divider}`,
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            flexShrink: 0,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {isLoading ? <CircularProgress size={21} /> : <Whatsapp />}
          </Box>
        </Box>
        <WhatsAppModal
          open={modalOpen && !hasConsent}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
        />
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          padding: '12px 16px',
          mb: 2,
        }}
      >
        <Typography
          sx={{
            color: theme.palette.text.secondary,
            fontSize: 12,
            fontWeight: 500,
            lineHeight: '15.6px',
            letterSpacing: '0.17px',
            textAlign: 'center',
          }}
        >
          Continue conversation on
        </Typography>
        <Box
          onClick={handleClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '8px 35px',
            background: theme.palette.background.paper,
            boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.08)',
            borderRadius: '8px',
            cursor: 'pointer',
            '&:hover': {
              background: theme.palette.action.hover,
            },
          }}
        >
          <Box
            sx={{
              width: 21,
              height: 21,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isLoading ? <CircularProgress size={21} /> : <Whatsapp />}
          </Box>
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              fontSize: 15,
              fontWeight: 500,
              lineHeight: '26px',
              letterSpacing: '0.46px',
            }}
          >
            WhatsApp
          </Typography>
        </Box>
      </Box>
      <WhatsAppModal
        open={modalOpen && !hasConsent}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
      />
    </>
  );
};

export default WhatsAppButton;